import { css, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import type { CodingAgentId } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/coding-agent-choice/types';
import {
	WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME,
} from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/common/constants';
import type { IssueTypeIconName } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';
import { JfindAgentAvatar } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-agent-avatar';
import { JfindIllustrationBackground } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-background';
import { JfindIllustrationBoardView } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-board-view';
import { JfindIllustrationCollaboratorCursor } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-collaborator-cursor';
import { JfindIllustrationListView } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-list-view';
import { JfindIllustrationProjectHeader } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-project-header';
import type { ViewType } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-types';

type Props = {
	projectName?: string;
	viewType?: ViewType;
	issueTypesIcons?: IssueTypeIconName[];
	columnNames?: string[];
	step?: string;
	selectedAgents?: CodingAgentId[];
};

/**
 * TWCG-1861 — JFind Onboard Illustration Refresh
 *
 * Replaces the static `ProjectSvg` on the right-hand side of every
 * onboarding wizard screen with a Jira project view illustration
 * (board or list, based on user elections).
 *
 * Experiment: `jfind-onboard-illustration-refresh-v2` (targeting gate `jfind-onboard-illustration-refresh-gate-v2`) — registered in manifest.ts.
 */
const messages = defineMessages({
	you: {
		id: 'software-onboarding-modals.welcome-modal.wizards.right-hand-side.illustration.you',
		defaultMessage: 'You',
		description: 'Label on the user cursor in the invite step illustration',
	},
});

export const JfindOnboardIllustrationRefresh = ({
	projectName = 'My Software Team',
	viewType = 'board',
	issueTypesIcons,
	columnNames,
	step,
	selectedAgents,
}: Props) => {
	const intl = useIntl();
	const isInviteStep = step === WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME;
	const isProjectDetailsStep =
		step === WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME || step === undefined;

	// For coding agent step: always pass an array (even empty) so board view uses fixed layout
	// Each entry is { id: string, avatar: ReactNode, delay: string } for card avatar pop-in timing
	const agentAvatars = selectedAgents
		? selectedAgents.map((agentId, index) => ({
				id: agentId,
				avatar: <JfindAgentAvatar key={agentId} agentId={agentId} size={16} />,
				delay: `${(index + 1) * 100}ms`,
			}))
		: undefined;

	return (
		<div css={outerStyles}>
			<JfindIllustrationBackground />

			{/* Background backdrop */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, local/ensure-wac-token-usage */}
			<Box xcss={backdropStyles} style={{ backgroundColor: 'rgba(68, 84, 111, 0.12)' }} />

			{/* Content panel */}
			<div css={contentPanelCss}>
				<JfindIllustrationProjectHeader
					projectName={projectName}
					viewType={viewType}
					isInviteStep={isInviteStep}
					selectedAgents={selectedAgents}
				/>
				{viewType === 'board' ? (
					<div css={boardContainerCss} key={`board-${isInviteStep ? 'invite' : 'default'}`}>
						<JfindIllustrationBoardView
							issueTypesIcons={issueTypesIcons}
							columnNames={columnNames}
							agentAvatars={agentAvatars}
							isInviteStep={isInviteStep}
							animateCardsOnMount={isProjectDetailsStep}
						/>
						{isInviteStep && (
							<>
								<JfindIllustrationCollaboratorCursor
									color={token('color.icon.accent.lime')}
									label={intl.formatMessage(messages.you)}
									top={28}
									left={112}
									animationDelay="900ms"
								/>
								<JfindIllustrationCollaboratorCursor
									color="#FCA700"
									accentColor={token('color.text.accent.orange')}
									top={155}
									left={320}
									animationDelay="1000ms"
								/>
								<JfindIllustrationCollaboratorCursor
									color="#0C66E4"
									arrowDirection="top-left"
									top={220}
									left={115}
									animationDelay="1100ms"
								/>
							</>
						)}
					</div>
				) : (
					<div css={boardContainerCss} key={`list-${isInviteStep ? 'invite' : 'default'}`}>
						<JfindIllustrationListView
							issueTypesIcons={issueTypesIcons}
							columnNames={columnNames}
						/>
						{isInviteStep && (
							<>
								<JfindIllustrationCollaboratorCursor
									color={token('color.icon.accent.lime')}
									label={intl.formatMessage(messages.you)}
									top={28}
									left={112}
									animationDelay="900ms"
								/>
								<JfindIllustrationCollaboratorCursor
									color="#FCA700"
									accentColor={token('color.text.accent.orange')}
									top={155}
									left={320}
									animationDelay="1000ms"
								/>
								<JfindIllustrationCollaboratorCursor
									color="#0C66E4"
									arrowDirection="top-left"
									top={220}
									left={115}
									animationDelay="1100ms"
								/>
							</>
						)}
					</div>
				)}
			</div>

			{/* Arrow — fades in on project details step, unmounts on other steps */}
			{isProjectDetailsStep && (
				<div css={arrowCss} aria-hidden="true">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="82"
						height="88"
						viewBox="0 0 88 94"
						fill="none"
					>
						<path
							d="M0.426127 83.769C-0.80959 79.724 10.0413 54.0343 14.2795 59.2702C11.3925 65.0928 10.3011 72.5522 8.24447 77.5955C8.36246 77.5532 8.31109 77.7608 8.36343 77.9501C9.19504 77.3569 11.1738 77.2814 11.806 76.3907C17.6581 75.0429 22.2542 71.4222 27.717 69.1202C28.0045 69.8573 28.3912 69.41 27.9402 69.0348C31.955 68.1681 56.0782 54.575 59.9278 45.6377C31.776 49.3825 56.1285 10.8412 65.9501 37.629C71.8798 34.4135 77.3649 28.8639 80.1359 22.6373C82.6822 17.1408 82.8428 11.4589 82.8209 8.09699C82.6953 4.75342 83.147 3.46699 84.8284 6.7336L85.2527 6.30113C86.0931 9.89991 86.4182 12.9211 86.7809 16.9862C86.6059 17.5334 84.8007 18.7357 84.5905 19.231C85.561 19.2913 85.8717 20.0671 85.7733 21.239C85.599 22.3938 85.0155 23.9449 84.0799 25.3873C82.2934 28.3516 79.3764 30.7374 78.8993 31.4022C80.1286 32.8674 76.6475 34.0152 76.5011 35.1346C76.6032 35.1409 77.2343 35.0933 77.3237 35.0989C77.4301 35.8619 73.942 38.361 70.7625 39.8128C69.0314 40.6477 67.6951 41.1103 66.766 41.623C67.1582 42.7387 66.4399 46.253 65.5392 48.578C62.8379 54.9205 52.2562 64.3077 52.3159 64.5842C48.3988 65.9406 44.1839 68.1713 40.4181 69.3635C33.5719 73.7125 11.1933 81.9205 9.18363 82.0809C10.1454 82.4878 11.5242 82.7843 13.0187 83.0755C13.0187 83.0755 16.2249 83.6715 16.8444 83.8092C16.8815 83.6254 17.0549 83.723 17.1639 83.6182C18.499 84.6188 21.3122 84.7315 21.7605 85.9746L26.3297 86.2956C37.4653 93.3737 6.30718 87.3212 1.97116 84.485L2.04933 84.465C2.04933 84.465 0.439171 83.559 0.425359 83.7813L0.426127 83.769ZM60.8443 40.9948C59.8519 38.1926 62.3834 42.2189 61.9341 43.4682C64.4716 44.0971 61.7872 35.7211 58.6302 34.3345C55.6116 32.1629 61.4862 39.5342 57.4857 35.2183C47.9776 34.1316 57.1153 43.8633 60.8443 40.9948Z"
							fill="#101214"
						/>
					</svg>
				</div>
			)}
		</div>
	);
};

const boardContainerCss = css({
	position: 'relative',
	flex: 1,
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});

const outerStyles = css({
	minHeight: '560px',
	position: 'relative',
	width: '100%',
	height: '100%',
	overflow: 'hidden',
	backgroundColor: wacToken('color.extended.blue100'),
});

const backdropStyles = xcss({
	position: 'absolute',
	insetInlineStart: '45px',
	insetBlockStart: '114px',
	width: '537px',
	height: '620px',
	borderStartStartRadius: token('radius.xlarge', '12px'),
	borderStartEndRadius: token('radius.xlarge', '12px'),
	borderEndStartRadius: token('radius.xlarge', '12px'),
	borderEndEndRadius: token('radius.xlarge', '12px'),
});

const contentPanelCss = css({
	position: 'absolute',
	insetInlineStart: '62px',
	insetBlockStart: '87px',
	insetInlineEnd: '0',
	height: '579px',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '12px',
	borderInlineStart: '2.1px solid var(--ds-border)',
	borderBlockStart: '2.1px solid var(--ds-border)',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});

const fadeInKeyframes = keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const arrowCss = css({
	position: 'absolute',
	insetInlineEnd: '120px',
	insetBlockStart: '39px',
	width: '83px',
	height: '89px',
	pointerEvents: 'none',
	animationName: fadeInKeyframes,
	animationDuration: '300ms',
	animationFillMode: 'forwards',
	animationTimingFunction: 'ease-in-out',
});
